import { useMemo, useState } from 'react'
import {
  countsFor,
  loadCatalog,
  loadMoves,
  MOVE_KINDS,
  newItem,
  NOT_IN_STOCK,
  onHand,
  refuse,
  saveCatalog,
  saveMoves,
  type CatalogItem,
  type MoveKind,
  type Movement,
} from '../lib/stock'
import { Empty, NotKnown, Sheet } from './bits'


/**
 * WHAT IS ON THE SHELF, AND HOW IT GOT THERE.
 *
 * INV-01. Three screens in this product have been saying there is no stock
 * count behind this since the day they were written: the materials takeoff,
 * the approval's pick list, and the milestone on Today that can never tick.
 * This is the count.
 *
 * ON HAND IS DERIVED, NEVER TYPED. There is no editable quantity anywhere on
 * this screen. Everything is a movement with a reason and a day on it, and
 * every number is a walk over that ledger, because a stored count is a number
 * that can disagree with what happened and stock is where that costs a
 * Saturday. Correcting a number means saying what you actually counted, which
 * is another movement, which is the only version of the spec's adjustments
 * require reason and ledger history that cannot be quietly edited away.
 *
 * UNKNOWN IS NOT ZERO, and it is printed as the word. An item nobody has
 * counted shows unknown, and the load check treats it as unknown rather than
 * refusing to load a van over a number nobody has ever entered.
 */
export default function Stock() {
  const [catalog, setCatalog] = useState<CatalogItem[]>(() => loadCatalog())
  const [moves, setMoves] = useState<Movement[]>(() => loadMoves())
  const [editing, setEditing] = useState<CatalogItem | null>(null)
  const [moving, setMoving] = useState<CatalogItem | null>(null)
  const [showLedger, setShowLedger] = useState<string | null>(null)

  const rows = useMemo(
    () =>
      catalog.map((c) => {
        const counts = countsFor(c.id, moves)
        return { item: c, counts, total: onHand(counts) }
      }),
    [catalog, moves],
  )

  const writeCatalog = (next: CatalogItem[]) => {
    setCatalog(next)
    saveCatalog(next)
  }
  const writeMoves = (next: Movement[]) => {
    setMoves(next)
    saveMoves(next)
  }

  const add = () => setEditing(newItem(`it_${Date.now().toString(36)}`))

  return (
    <>
      <div className="ws-head">
        <div>
          <h1>Stock</h1>
          <p>What is on the shelf and what is on the truck, worked out from what actually moved.</p>
        </div>
        <div className="ws-head-acts">
          <button className="btn primary" onClick={add}>
            Add a product
          </button>
        </div>
      </div>

      {/*
        THE PARAGRAPH IS NARROW AND THE TABLE IS NOT. The same split the
        settings screens use, for the same reason: prose is read and a table
        with five columns in it is scanned, and squeezing the second one to
        the width of the first put every product name on two lines.
      */}
      <p className="est-answer set-safe setpage">
        <b>Nothing here is a number you type.</b> Every count is worked out from the movements
        under it, so correcting one means saying what you actually counted rather than
        overwriting the history. An item nobody has counted reads unknown, never zero.
      </p>

      <div className="stock-wrap">
        {catalog.length === 0 ? (
          <div className="ws-card">
            <Empty title="No products yet" action={{ label: 'Add a product', onClick: add }}>
              Add the first one and the count starts the moment something arrives.
            </Empty>
          </div>
        ) : (
          <div className="ws-card stock-list">
            <div className="ws-thead stock-head" aria-hidden="true">
              <span>Product</span>
              <span>Warehouse</span>
              <span>Truck</span>
              <span>On hand</span>
              <span />
            </div>
            {rows.map(({ item, counts, total }) => (
              <div key={item.id} className="stock-item">
                <div className="stock-row">
                  <span className="stock-name">
                    <strong>{item.name.trim() || 'Unnamed product'}</strong>
                    <span>
                      {[item.sku, item.unit, item.packSize > 0 ? `${item.packSize} to a box` : '']
                        .filter(Boolean)
                        .join('  ·  ')}
                    </span>
                  </span>
                  {/*
                    UNKNOWN IS A WORD, NOT A DASH AND NOT A ZERO. A dash is
                    read as none by everybody who has ever seen a spreadsheet.
                  */}
                  <span className="stock-n wh">{counts ? counts.warehouse : <em>unknown</em>}</span>
                  <span className="stock-n tr">{counts ? counts.truck : <em>unknown</em>}</span>
                  <span className="stock-n total">{total === null ? <em>unknown</em> : total}</span>
                  {/*
                    ALL THREE ON ONE LINE. History sat on a line of its own
                    under every row, which cost forty pixels a product for one
                    word and made a list of twenty into a scroll.
                  */}
                  <span className="stock-acts">
                    <button className="btn" onClick={() => setMoving(item)}>
                      Record a move
                    </button>
                    <button
                      className="link-btn"
                      onClick={() => setShowLedger(showLedger === item.id ? null : item.id)}
                    >
                      {showLedger === item.id ? 'Hide' : 'History'}
                    </button>
                    <button className="link-btn" onClick={() => setEditing(item)}>
                      Edit
                    </button>
                  </span>
                </div>
                {showLedger === item.id && (
                  <Ledger
                    moves={moves.filter((m) => m.itemId === item.id)}
                    unit={item.unit}
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <NotKnown title="What stock does not do here" items={NOT_IN_STOCK} />

      {editing && (
        <ItemSheet
          item={editing}
          onClose={() => setEditing(null)}
          onSave={(next) => {
            writeCatalog(
              catalog.some((c) => c.id === next.id)
                ? catalog.map((c) => (c.id === next.id ? next : c))
                : [...catalog, next],
            )
            setEditing(null)
          }}
        />
      )}

      {moving && (
        <MoveSheet
          item={moving}
          moves={moves}
          onClose={() => setMoving(null)}
          onSave={(m) => {
            writeMoves([...moves, m])
            setMoving(null)
          }}
        />
      )}
    </>
  )
}

/** The history, newest first, because the question is what happened lately. */
function Ledger({ moves, unit }: { moves: Movement[]; unit: string }) {
  const list = [...moves].sort((a, b) => (a.day < b.day ? 1 : -1))
  if (list.length === 0) return <p className="hint stock-empty">Nothing has moved yet.</p>
  return (
    <div className="stock-ledger">
      {list.map((m) => (
        <p key={m.id}>
          <b>{m.day}</b>
          <span>{MOVE_KINDS.find((k) => k.id === m.kind)?.label}</span>
          <em>
            {m.qty} {unit}
          </em>
          {m.why.trim() && <i>{m.why}</i>}
        </p>
      ))}
    </div>
  )
}

/** The product itself. What it is called, what one of it is, how it is boxed. */
function ItemSheet({
  item,
  onClose,
  onSave,
}: {
  item: CatalogItem
  onClose: () => void
  onSave: (c: CatalogItem) => void
}) {
  const [c, setC] = useState(item)
  return (
    <Sheet
      title={item.name.trim() || 'A new product'}
      variant="vsheet"
      onClose={onClose}
    >
      <div className="vsheet-body">
        <div className="ws-form vsheet-form">
          <label className="ws-wide">
            <span>What it is called</span>
            <input
              value={c.name}
              placeholder="C9 warm white"
              onChange={(e) => setC({ ...c, name: e.target.value })}
            />
          </label>
          <label>
            <span>Your part number</span>
            <input
              value={c.sku}
              placeholder="Optional"
              onChange={(e) => setC({ ...c, sku: e.target.value })}
            />
          </label>
          <label>
            <span>What one of it is</span>
            <input
              value={c.unit}
              placeholder="bulbs, clips, feet"
              onChange={(e) => setC({ ...c, unit: e.target.value })}
            />
          </label>
          <label className="ws-wide">
            <span>How many come in a box</span>
            <input
              type="number"
              min={0}
              value={c.packSize || ''}
              placeholder="Leave empty if it is not boxed"
              onChange={(e) => setC({ ...c, packSize: Number(e.target.value) })}
            />
          </label>
        </div>
      </div>
      <div className="ws-actions">
        <button className="btn" onClick={onClose}>
          Cancel
        </button>
        <button className="btn primary" disabled={!c.name.trim()} onClick={() => onSave(c)}>
          Save
        </button>
      </div>
    </Sheet>
  )
}

/**
 * ONE MOVEMENT.
 *
 * The refusal is checked as it is typed rather than on the way out, because
 * the number somebody is about to enter is the number they can still change.
 * It names what the location would have gone to: minus fourteen clips is a
 * fact a person can act on, and "not enough" is not.
 */
function MoveSheet({
  item,
  moves,
  onClose,
  onSave,
}: {
  item: CatalogItem
  moves: Movement[]
  onClose: () => void
  onSave: (m: Movement) => void
}) {
  const today = new Date().toISOString().slice(0, 10)
  const [kind, setKind] = useState<MoveKind>('received')
  const [qty, setQty] = useState(0)
  const [day, setDay] = useState(today)
  const [why, setWhy] = useState('')

  const draft = { itemId: item.id, kind, qty, day, why, jobId: '' }
  const bad = refuse(draft, moves)
  const counts = countsFor(item.id, moves)

  return (
    <Sheet
      title={item.name.trim() || 'This product'}
      variant="vsheet"
      onClose={onClose}
    >
      <div className="vsheet-body">
        <p className="hint stock-now">
          {counts
            ? `${counts.warehouse} in the warehouse, ${counts.truck} on the truck.`
            : 'Nothing counted yet. Receiving some, or counting the shelf, is how that starts.'}
        </p>
        <div className="ws-form vsheet-form">
          <label className="ws-wide">
            <span>What happened</span>
            <select value={kind} onChange={(e) => setKind(e.target.value as MoveKind)}>
              {MOVE_KINDS.map((k) => (
                <option key={k.id} value={k.id}>
                  {k.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>How many {item.unit}</span>
            <input
              type="number"
              min={0}
              value={qty || ''}
              onChange={(e) => setQty(Number(e.target.value))}
            />
          </label>
          <label>
            <span>The day it happened</span>
            <input type="date" value={day} onChange={(e) => setDay(e.target.value)} />
          </label>
          <label className="ws-wide">
            <span>{kind === 'counted' ? 'Why you counted' : 'Anything worth remembering'}</span>
            <input
              value={why}
              placeholder={kind === 'counted' ? 'Shelf count, season start' : 'Optional'}
              onChange={(e) => setWhy(e.target.value)}
            />
          </label>
        </div>
        {bad && <p className="set-bad">{bad}</p>}
      </div>
      <div className="ws-actions">
        <button className="btn" onClick={onClose}>
          Cancel
        </button>
        <button
          className="btn primary"
          disabled={!!bad}
          onClick={() =>
            onSave({
              ...draft,
              qty: Math.max(0, Math.round(qty)),
              id: `mv_${Date.now().toString(36)}`,
              at: new Date().toISOString(),
            })
          }
        >
          Record it
        </button>
      </div>
    </Sheet>
  )
}
